'use client';

import React from 'react';
import { usePlatformComponents } from './usePlatformComponents';
import { WithPlatformComponents } from '../interfaces/WithPlatformComponents';

/**
 * @group Functions

@summary
<blockquote>Higher-order component that injects platform primitives into universal components.</blockquote>

@example
```typescript
const LogoWithPlatforms = withPlatformComponents(LogoComponent);
```
 * @internal
 */
export const withPlatformComponents = <P extends object>(
  WrappedComponent: React.ComponentType<P & WithPlatformComponents>
): React.ComponentType<P> => {
  const ComponentWithPlatform = (props: P) => {
    const componentsResult = usePlatformComponents();
    
    return componentsResult.match({
      ok: (components) => (
        <WrappedComponent 
          {...props} 
          components={components}
        />
      ),
      err: (error) => {
        return null;
      }
    });
  };

  ComponentWithPlatform.displayName = `withPlatformComponents(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return ComponentWithPlatform;
}; 